import axios from "axios";
import { Vector3 } from "three";
import { config } from "../../config/config";
import { ITextScale } from "../config/ViewerConfig";
import { PreviewManager } from "../object/PreviewManager";
import { InputHandler } from "../utils/input-handler/input-handler";
import { Viewer } from "../viewer";
import { AreaMeasurementCreationPreview } from "./AreaMeasurementCreationPreview";
import { FlatAreaCalculationMeasurement } from "./FlatAreaCalculationMeasurement";
import { HorizontalMeasurement } from "./HorizontalMeasurement";
import { MeasurementEditObservable } from "./MeasurementEditObservable";
import { MultiplePointMeasurement } from "./MultiplePointMeasurement";
import { MultiplePointMeasurementCreationPreview } from "./MultiplePointMeasurementCreationPreview";
import { SurfaceMeasurement } from "./SurfaceMeasurement";
import { TwoPointMeasurement } from "./TwoPointMeasurement";
import { TwoPointMeasurementCreationPreview } from "./TwoPointMeasurementCreationPreview";
import {
  ICreateVolumeMeasurementParams,
  IEditMeasurementParams,
  IMeasurement,
  IMeasurementHandler,
  IMeasurementPreviewCreation,
  IMeasurementVisibleParams,
  IPanelDraggable,
  MeasurementMode,
  MeasurementState,
  MeasurementType,
  TCreateMeasurementParams,
  TMeasurementEditObserver,
} from "./types";
import { VerticalMeasurement } from "./VerticalMeasurement";
import { VolumeCalculationMeasurement } from "./VolumeCalculationMeasurement";
import { restorePanelPositionIfPresent } from "./restorePanelPositionIfPresent";
import { toMeasurementUploadPayload } from "./measurementUploadPayload";
import { EUpdateStatus } from "../../components/ProjectDetail/Interfaces/ProjectDetailInterfaces";
import {
  LastSavedPointsCache,
  recordLastSavedPoints,
  snapshotMeasurementPoints,
  syncLastSavedPointsFromMeasurements,
} from "./lastSavedPointsCache";
import { restoreActiveMeasurementFromCache } from "./restoreLastSavedPoints";

export class MeasurementManager {
  public measurementMaps: Map<number, IMeasurementHandler>;
  public activeMeasurement: IMeasurementHandler;

  private _viewer: Viewer;
  private _preview: PreviewManager;

  private _inputHandler: InputHandler;

  public isMeasuring = false;
  public measurementPreview: IMeasurementPreviewCreation;
  private _editObservable: MeasurementEditObservable;

  private _currentMeasurementId: number = 0;
  private _lastSavedPoints: LastSavedPointsCache = new Map();

  constructor(
    viewer: Viewer,
    preview: PreviewManager,
    inputHandler: InputHandler
  ) {
    this._viewer = viewer;
    this._preview = preview;
    this.measurementMaps = new Map();
    this._inputHandler = inputHandler;
    this._editObservable = new MeasurementEditObservable(this._viewer);
  }

  public getLastSavedPoints(id: number): Vector3[] | undefined {
    return this._lastSavedPoints.get(id);
  }

  /**
   * QU3DW-1399 (SC_QUICK-2063) — record the just-saved points as the new
   * restore baseline. Call this after ANY successful save to the server, not
   * only updateEditMeasurement — CalcDrawerContent.tsx's uploadCalcResult()
   * (new measurement, visibility toggle) and deleteCalcResult() call this
   * directly since they PUT to the server without going through
   * updateEditMeasurement().
   */
  public recordSavedMeasurements(
    measurements: Iterable<{ id: number; _points: Vector3[] }>
  ): void {
    syncLastSavedPointsFromMeasurements(this._lastSavedPoints, measurements);
  }

  public generateMeasurementId(): number {
    return ++this._currentMeasurementId;
  }

  public setMeasurementId(id: number) {
    this._currentMeasurementId = id;
  }

  public initMeasurementMap() {
    this.measurementMaps = new Map();
  }

  public async createMeasurement(
    params: TCreateMeasurementParams,
    textScale: ITextScale
  ): Promise<IMeasurementHandler> {
    if (this.measurementPreview) {
      this.measurementPreview.finishMeasurement(false, "Measurement Cancelled");
      this.measurementPreview.dispose();
      this.measurementPreview = undefined;
      this.activeMeasurement = undefined;
    }
    this.activeMeasurement = undefined;
    this.isMeasuring = true;
    switch (params.type) {
      case MeasurementType.VOLUME_CALCULATION:
      case MeasurementType.FLAT_AREA_CALCULATION:
        this.measurementPreview = new AreaMeasurementCreationPreview(
          this._viewer,
          this._preview
        );
        break;
      case MeasurementType.TWO_POINT:
      case MeasurementType.HORIZONTAL:
      case MeasurementType.VERTICAL:
      case MeasurementType.SURFACE:
        this.measurementPreview = new TwoPointMeasurementCreationPreview(
          this._viewer,
          this._preview
        );
        break;
      case MeasurementType.MULTIPLE_POINT:
        this.measurementPreview = new MultiplePointMeasurementCreationPreview(
          this._viewer,
          this._preview,
          textScale
        );
        break;
      default:
        throw { message: `${params.type} is not supported` };
    }

    this._inputHandler.registerHandler(
      `preview_${this.measurementPreview.id}`,
      this.measurementPreview
    );

    try {
      const points = await this.measurementPreview.startMeasurement();

      this._inputHandler.unregisterHandler(
        `preview_${this.measurementPreview.id}`
      );
      this.measurementPreview = undefined;
      if (points && points.length >= 2) {
        let measurement: IMeasurementHandler;
        switch (params.type) {
          case MeasurementType.TWO_POINT: {
            measurement = new TwoPointMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.HORIZONTAL: {
            measurement = new HorizontalMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.VERTICAL: {
            measurement = new VerticalMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.SURFACE: {
            measurement = new SurfaceMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.VOLUME_CALCULATION: {
            measurement = new VolumeCalculationMeasurement(
              this._viewer,
              params as ICreateVolumeMeasurementParams,
              textScale
            );
            break;
          }
          case MeasurementType.FLAT_AREA_CALCULATION: {
            measurement = new FlatAreaCalculationMeasurement(
              this._viewer,
              textScale
            );
            break;
          }
          case MeasurementType.MULTIPLE_POINT: {
            measurement = new MultiplePointMeasurement(this._viewer, textScale);
          }
        }

        this.activeMeasurement = measurement;
        measurement.setPoints(points);
        this.measurementMaps.set(measurement.id, measurement);
        return measurement;
      }
    } catch ({ message, id }: any) {
      // console.log("cancel", `preview_${id}`);
      this._inputHandler.unregisterHandler(`preview_${id}`);
      this._viewer.updateRenderer();
      throw { message, id };
    }
  }

  public editMeasurement(
    id: number,
    params: IEditMeasurementParams,
    observers: TMeasurementEditObserver[]
  ): void {
    this.isMeasuring = true;
    this.activeMeasurement = undefined;
    const measurement = this.measurementMaps.get(id);

    if (measurement) {
      this.activeMeasurement = measurement;
      this._inputHandler.registerHandler(
        "edit-measurement",
        this._editObservable
      );
      measurement.switchMode(MeasurementMode.EDIT);
      this._editObservable.startEditing(measurement, params, observers);
    }
  }

  public async createInitialMeasurement(
    params: TCreateMeasurementParams,
    data: IMeasurement,
    textScale: ITextScale
  ): Promise<IMeasurementHandler> {
    try {
      const points = data._points;
      if (points && points.length >= 2) {
        let measurement: IMeasurementHandler;
        switch (params.type) {
          case MeasurementType.TWO_POINT: {
            measurement = new TwoPointMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.HORIZONTAL: {
            measurement = new HorizontalMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.VERTICAL: {
            measurement = new VerticalMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.SURFACE: {
            measurement = new SurfaceMeasurement(this._viewer, textScale);
            break;
          }
          case MeasurementType.VOLUME_CALCULATION: {
            measurement = new VolumeCalculationMeasurement(
              this._viewer,
              {
                type: MeasurementType.VOLUME_CALCULATION,
                lowerBound: {
                  minColor: 0xa5e5ff,
                  maxColor: 0x0000ff,
                },
                upperBound: {
                  minColor: 0xffff00,
                  maxColor: 0xff0000,
                },
              },
              textScale
            );
            break;
          }
          case MeasurementType.FLAT_AREA_CALCULATION: {
            measurement = new FlatAreaCalculationMeasurement(
              this._viewer,
              textScale
            );
            break;
          }
          case MeasurementType.MULTIPLE_POINT: {
            measurement = new MultiplePointMeasurement(this._viewer, textScale);
            break;
          }
        }
        const vectorPoints: Vector3[] = [];
        for (const val of points) {
          const vectorPoint = new Vector3(val.x, val.y, val.z);
          vectorPoints.push(vectorPoint);
        }
        measurement.setPoints(vectorPoints);
        recordLastSavedPoints(this._lastSavedPoints, measurement.id, vectorPoints);
        this.measurementMaps.set(measurement.id, measurement);
        // Restore the saved Measurement Result panel position, if any (SC_QUICK-2187, QU3DW-1367)
        restorePanelPositionIfPresent(
          measurement as unknown as IPanelDraggable,
          data
        );
        //After getting measurement list from BE, if any measurement is not visible (visible field is false), we should set visibility of measurement to false
        if (data?.visible === false) {
          measurement.setVisibility({
            text: false,
            measurement: false,
          });
        }
        return measurement;
      }
    } catch ({ message, id }: any) {
      // console.log("cancel", `preview_${id}`);
      // this._inputHandler.unregisterHandler(`preview_${id}`);
      this._viewer.updateRenderer();
      throw { message, id };
    }
  }

  public setPoints(id: number, points: Vector3[]) {
    if (id) {
      //editMode
      const measurement = this.measurementMaps.get(id);
      if (measurement) {
        measurement.setPoints(points);
      }
    } else {
      //creation mode
      if (this.measurementPreview) this.measurementPreview.setPoints(points);
    }
  }

  public deleteMeasurement(ids?: number | number[]): number | number[] {
    const deletedIds: number[] = [];
    const _deleteMeasurement = (measurement: IMeasurementHandler) => {
      if (this.activeMeasurement?.id === measurement.id) {
        this.activeMeasurement = undefined;
      }
      if (
        measurement &&
        measurement.type === MeasurementType.VOLUME_CALCULATION
      ) {
        this._viewer._pointCloudManager.setHeatMapVisibility(false);
        const measurements = this.getMeasurementByType(
          MeasurementType.VOLUME_CALCULATION
        );
        for (const m of measurements as VolumeCalculationMeasurement[]) {
          if (m.id !== measurement.id) {
            m.heatMapVisibility &&
              this._viewer.applyHeightBasedColor(
                m.polygonMesh,
                m.pointColorRangeInfo
              );
          }
        }
      }
      deletedIds.push(measurement.id);
      measurement.dispose();
      measurement = undefined;
    };
    if (ids !== undefined) {
      if (Array.isArray(ids)) {
        for (const id of ids) {
          let measurement = this.measurementMaps.get(id);
          measurement && _deleteMeasurement(measurement);
          this.measurementMaps.delete(id);
        }
      } else {
        let measurement = this.measurementMaps.get(ids);
        measurement && _deleteMeasurement(measurement);
        this.measurementMaps.delete(ids);
      }
    } else {
      this.measurementMaps.forEach((measurement) => {
        _deleteMeasurement(measurement);
      });
      this.measurementMaps.clear();
    }
    if (this.measurementMaps.size === 0) {
      this._currentMeasurementId = 0; //when deleted all, id should start from 1
    }
    return deletedIds.length === 1 ? deletedIds[0] : deletedIds;
  }

  public changeAppearanceByState(id: number, state: MeasurementState) {
    if (state === "selected") {
      for (let [key, measurement] of this.measurementMaps.entries()) {
        if (key === id) {
          measurement.changeAppearanceState(state);
        } else {
          measurement.changeAppearanceState("default");
        }
      }
    } else if (state === "hovered" || state === "default") {
      for (let [key, measurement] of this.measurementMaps.entries()) {
        if (key === id && measurement.appearanceState !== "selected") {
          measurement.changeAppearanceState(state);
        } else {
          if (measurement.appearanceState === "hovered") {
            measurement.changeAppearanceState("default");
          }
        }
      }
    }
    this._viewer.updateRenderer();
  }

  public cancelMeasurement() {
    if (this.measurementPreview) {
      this.measurementPreview.dispose();
      this._viewer.updateRenderer(1);
      this.isMeasuring = false;
    }
  }

  public finishMeasurement() {
    this.activeMeasurement?.switchMode(MeasurementMode.INACTIVE);
    this.measurementPreview?.finishMeasurement();
    this._inputHandler.unregisterHandler("edit-measurement");

    this.isMeasuring = false;
    if (this.activeMeasurement) {
      this.changeAppearanceByState(this.activeMeasurement?.id, "selected");
    }
  }

  public cancelEditingMeasurement() {
    restoreActiveMeasurementFromCache(
      this.activeMeasurement,
      this._lastSavedPoints
    );
    this.activeMeasurement?.switchMode(MeasurementMode.INACTIVE);
    this._inputHandler.unregisterHandler("edit-measurement");
    this.isMeasuring = false;
    if (this.activeMeasurement) {
      this.changeAppearanceByState(this.activeMeasurement.id, "selected");
    }
  }

  public resetMeasurement() {
    this.measurementPreview?.reset();
  }

  public unmark() {
    this.measurementPreview?.unmark();
  }

  public async updateEditMeasurement(
    project_id: string,
    updateAt?: string,
    setUpdateAt?: (value: string) => void
  ) {
    const beforeUpdateAt = updateAt;
    const afterUpdateAt = new Date().toISOString();
      
    const calcNewArray: IMeasurement[] = toMeasurementUploadPayload(
      Array.from(this.measurementMaps.values()),
      this._viewer,
      afterUpdateAt
    );

    const savedSnapshot = snapshotMeasurementPoints(this.measurementMaps.values());
    setUpdateAt(afterUpdateAt);
    await axios
      .put(
        `${
          config.serverHost
        }/api/v1/project/${project_id}/point-cloud/${this._viewer.getCurrentType()}/measurement`,
        { measurements: calcNewArray },
        { withCredentials: true }
      )
      .then((data) => {
        this.recordSavedMeasurements(savedSnapshot);
        setUpdateAt("saved");

        setTimeout(() => {
          setUpdateAt(afterUpdateAt);
        }, 3000);
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 403) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_FORBIDDEN);
          } else if (err.response.status === 500) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_ERROR);
          } else {
            setUpdateAt(EUpdateStatus.SAVE_FAILED);
          }
        }

        setTimeout(() => {
          setUpdateAt(beforeUpdateAt);
        }, 3000);
      });
  }

  public setMeasurementVisibility(
    id: number,
    visibleParams: IMeasurementVisibleParams
  ) {
    const measurement = this.measurementMaps.get(id);
    if (measurement) {
      measurement.setVisibility(visibleParams);
    }
  }

  public getMeasurementById(id: number) {
    return this.measurementMaps.get(id);
  }

  public getMeasurementByType(type: MeasurementType): IMeasurement[] {
    const measurements: IMeasurement[] = [];
    this.measurementMaps.forEach((measurement) => {
      if (measurement.type === type) {
        measurements.push(measurement);
      }
    });
    return measurements;
  }
}
